import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';

const KEYFRAMES = `
@keyframes srFadeUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes srScaleIn {
  from { opacity: 0; transform: scale(0.82); }
  to   { opacity: 1; transform: scale(1); }
}
@keyframes srConfetti {
  0%   { transform: translateY(-10px) rotate(0deg);   opacity: 1; }
  100% { transform: translateY(105vh) rotate(720deg); opacity: 0; }
}
@keyframes srGoldPulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(212,175,55,0); }
  50%      { box-shadow: 0 0 24px 4px rgba(212,175,55,0.35); }
}
`;

const AWARD_CONFIGS = [
  { key: 'seasonLeaderboardWinner', emoji: '🏆', title: 'Season Leaderboard Winner', statLabel: 'Avg Points', statKey: 'totalPoints', bg: 'rgba(212,175,55,0.07)' },
  { key: 'seasonFinaleWinner', emoji: '⭐', title: 'Season Finale Winner', statLabel: 'Net Gross', statKey: 'netGross', bg: 'rgba(212,175,55,0.07)', statSuffix: ' strokes' },
  { key: 'mostImproved',   emoji: '📈', title: 'Most Improved',      statLabel: 'Improvement',    statKey: 'improvement',  bg: 'rgba(16,185,129,0.06)',  statSuffix: ' pts' },
  { key: 'mostGrossBirdies', emoji: '🐦', title: 'Birdie King',      statLabel: 'Birdies',        statKey: 'count',        bg: 'rgba(16,185,129,0.06)' },
  { key: 'bestSingleRound',emoji: '🎯', title: 'Best Single Round',  statLabel: 'Points',         statKey: 'points',       bg: 'rgba(245,158,11,0.06)', hasNote: true },
  { key: 'ironMan',        emoji: '💪', title: 'Iron Man',           statLabel: 'Rounds Played',  statKey: 'roundsPlayed', bg: 'rgba(239,68,68,0.06)'  },
  { key: 'mostConsistent', emoji: '🎪', title: 'Most Consistent',    statLabel: 'Rounds at 16+',  statKey: 'count',        bg: 'rgba(139,92,246,0.06)' },
  { key: 'mostBeers',      emoji: '🍺', title: 'Most Beers',         statLabel: 'Beers',          statKey: 'count',        bg: 'rgba(251,191,36,0.06)' },
  { key: 'mostCigarettes', emoji: '🚬', title: 'Most Cigarettes',    statLabel: 'Cigarettes',     statKey: 'count',        bg: 'rgba(107,114,128,0.08)' },
];

const CONFETTI_COLORS = ['#d4af37','#10b981','#6366f1','#ef4444','#f59e0b','#ffffff','#ec4899','#06b6d4','#84cc16','#f97316'];

function Confetti() {
  const pieces = Array.from({ length: 26 }, (_, i) => ({
    left:     `${(i * 3.9 + 1) % 100}%`,
    width:    `${8 + (i % 6) * 3}px`,
    height:   `${8 + (i % 6) * 3}px`,
    bg:       CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    delay:    `${(i * 0.12) % 2.5}s`,
    duration: `${2.4 + (i % 4) * 0.4}s`,
    radius:   i % 3 === 0 ? '50%' : i % 3 === 1 ? '2px' : '0%',
  }));

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 3100, overflow: 'hidden' }}>
      {pieces.map((p, i) => (
        <div key={i} style={{
          position: 'absolute',
          top: '-20px',
          left: p.left,
          width: p.width,
          height: p.height,
          backgroundColor: p.bg,
          borderRadius: p.radius,
          animation: `srConfetti ${p.duration} ${p.delay} ease-in forwards`,
        }} />
      ))}
    </div>
  );
}

function PersonalCard({ card, visible }) {
  return (
    <div style={{
      backgroundColor: '#1e1e1e',
      border: '1px solid #2d2d2d',
      borderRadius: '16px',
      padding: 'clamp(20px, 4vw, 36px) clamp(16px, 5vw, 28px)',
      textAlign: 'center',
      maxWidth: '380px',
      width: '100%',
      margin: '0 auto',
      boxSizing: 'border-box',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: 'opacity 0.45s ease, transform 0.45s ease',
      animation: visible ? 'srGoldPulse 3s ease infinite' : 'none',
    }}>
      <div style={{ fontSize: 'clamp(36px, 9vw, 52px)', marginBottom: 'clamp(10px, 2vw, 18px)', lineHeight: 1 }}>{card.icon}</div>
      <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px' }}>
        {card.label}
      </div>
      <div style={{ fontSize: 'clamp(24px, 6vw, 38px)', fontWeight: '700', color: '#d4af37', letterSpacing: '-0.03em', marginBottom: '8px', lineHeight: 1.1 }}>
        {card.value}
      </div>
      {card.sub && (
        <div style={{ fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#9ca3af', fontWeight: '400', marginTop: '4px', lineHeight: 1.5 }}>{card.sub}</div>
      )}
    </div>
  );
}

function AwardCard({ config, award, visible, index, total }) {
  const rawStat = award?.[config.statKey];
  const statValue = rawStat ?? '—';
  const displayStat = (config.statSuffix && rawStat != null) ? `${statValue}${config.statSuffix}` : statValue;

  // Extra context line under the stat, per award type
  let subtitle = null;
  if (award) {
    if (config.key === 'ironMan' && award.isMultiple) {
      subtitle = `${award.roundsPlayed} rounds each`;
    } else if (config.key === 'mostGrossBirdies' && award.perRound != null) {
      subtitle = `${award.perRound} birdies per round · ${award.roundsPlayed} rounds`;
    } else if (config.key === 'mostConsistent' && award.count != null) {
      subtitle = `${award.count} of ${award.roundsPlayed} rounds (${award.pct}%) · ${award.avg} avg pts`;
    } else if (config.key === 'mostImproved' && award.firstAvg != null && award.secondAvg != null) {
      subtitle = `${award.firstAvg} → ${award.secondAvg} avg`;
    }
  }

  return (
    <div style={{
      borderRadius: '16px',
      padding: 'clamp(22px, 4vw, 40px) clamp(16px, 5vw, 32px)',
      textAlign: 'center',
      maxWidth: '480px',
      width: '100%',
      margin: '0 auto',
      boxSizing: 'border-box',
      background: `linear-gradient(145deg, #1e1e1e 0%, ${config.bg} 100%)`,
      border: '1px solid #2d2d2d',
      opacity: visible ? 1 : 0,
      transform: visible ? 'scale(1)' : 'scale(0.82)',
      transition: 'opacity 0.45s cubic-bezier(0.34,1.56,0.64,1), transform 0.45s cubic-bezier(0.34,1.56,0.64,1)',
      boxShadow: visible ? '0 12px 48px rgba(0,0,0,0.55)' : 'none',
    }}>
      <div style={{ fontSize: 'clamp(52px, 14vw, 80px)', marginBottom: 'clamp(12px, 2.5vw, 22px)', display: 'block', lineHeight: 1 }}>{config.emoji}</div>
      <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px' }}>
        {config.title}
      </div>
      {award ? (
        <>
          <div style={{ fontSize: 'clamp(18px, 5vw, 26px)', fontWeight: '700', color: '#ffffff', letterSpacing: '-0.02em', marginBottom: '8px' }}>
            {award.name ?? '—'}
          </div>
          <div style={{ fontSize: 'clamp(14px, 3.5vw, 18px)', fontWeight: '600', color: '#d4af37' }}>
            {config.statLabel}: {displayStat}
          </div>
          {subtitle && (
            <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '6px' }}>{subtitle}</div>
          )}
          {config.hasNote && award.eventName && (
            <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '6px' }}>{award.eventName}</div>
          )}
        </>
      ) : (
        <div style={{ fontSize: '15px', color: '#6b7280', fontStyle: 'italic' }}>No data available</div>
      )}
      <div style={{ marginTop: 'clamp(16px, 3vw, 28px)', fontSize: '12px', color: '#374151', fontWeight: '600', letterSpacing: '0.05em' }}>
        {index + 1} / {total}
      </div>
    </div>
  );
}

export default function SeasonRecap({ onClose, currentUserId, seasonId }) {
  const [recapData, setRecapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('personal'); // 'personal' | 'awards'
  const [personalIndex, setPersonalIndex] = useState(0);
  const [cardVisible, setCardVisible] = useState(false);
  const [awardIndex, setAwardIndex] = useState(0);
  const [awardVisible, setAwardVisible] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // Build personal cards from playerStats (defined early so effects can use .length)
  const myStats = recapData?.playerStats?.[currentUserId];
  const sd = myStats?.scoreDistribution;
  const personalCards = myStats ? [
    // Only show the position card when the player has a finale position
    ...(myStats.finalPosition != null ? [{
      icon: '🏆',
      label: 'Your Final Position',
      value: `#${myStats.finalPosition}`,
      sub: 'season finale result',
    }] : []),
    {
      icon: '🏌️',
      label: 'Rounds Played',
      value: myStats.roundsPlayed ?? '—',
      sub: `avg ${myStats.avgPoints ?? '—'} pts/round · best: ${myStats.bestRound ?? '—'} pts`,
    },
    {
      icon: '🐦',
      label: 'Score Breakdown',
      value: `${sd?.birdie ?? 0} birdies`,
      sub: `🦅 ${sd?.eagle ?? 0} eagles · ⚪ ${sd?.par ?? 0} pars · ⚠️ ${sd?.bogey ?? 0} bogeys · ❌ ${sd?.double ?? 0} doubles`,
    },
    {
      icon: '📍',
      label: 'Ball Striking',
      value: myStats.girPct != null ? `${myStats.girPct}% GIR` : '—',
      sub: [
        myStats.par3avg != null ? `Par 3: ${myStats.par3avg}` : null,
        myStats.par4avg != null ? `Par 4: ${myStats.par4avg}` : null,
        myStats.par5avg != null ? `Par 5: ${myStats.par5avg}` : null,
      ].filter(Boolean).join(' · ') || 'no hole data',
    },
    {
      icon: '⛳',
      label: 'Favourite Course',
      value: myStats.favoriteCourse ?? '—',
      sub: `most rounds played here`,
    },
    {
      icon: '🍺',
      label: 'Club Life',
      value: `${myStats.totalBeers ?? 0} beers`,
      sub: `🚬 ${myStats.totalCigs ?? 0} cigarettes this season`,
    },
  ] : [];

  // Inject CSS keyframes once
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = KEYFRAMES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  // Load recap from Firestore
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'season_recaps', seasonId));
        if (snap.exists()) setRecapData(snap.data());
      } catch (err) {
        console.error('Error loading season recap:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [seasonId]);

  // Show first personal card after data loads
  useEffect(() => {
    if (!loading && recapData && phase === 'personal') {
      const t = setTimeout(() => setCardVisible(true), 150);
      return () => clearTimeout(t);
    }
  }, [loading, recapData, phase]);

  // Auto-advance personal cards every 3s
  useEffect(() => {
    if (phase !== 'personal' || !cardVisible) return;
    if (personalCards.length === 0) return;

    const t = setTimeout(() => {
      if (personalIndex < personalCards.length - 1) {
        setCardVisible(false);
        setTimeout(() => { setPersonalIndex(i => i + 1); setCardVisible(true); }, 300);
      } else {
        goToAwards();
      }
    }, 3000);
    return () => clearTimeout(t);
  }, [phase, cardVisible, personalIndex, personalCards.length]);

  // Award card animation
  useEffect(() => {
    if (phase !== 'awards') return;
    setAwardVisible(false);
    const t = setTimeout(() => {
      setAwardVisible(true);
      if (awardIndex === AWARD_CONFIGS.length - 1) {
        setTimeout(() => setShowConfetti(true), 500);
      } else {
        setShowConfetti(false);
      }
    }, 60);
    return () => clearTimeout(t);
  }, [awardIndex, phase]);

  const goToAwards = () => {
    setPhase('awards');
    setAwardIndex(0);
    setShowConfetti(false);
  };

  const handleNextPersonal = () => {
    if (personalIndex < personalCards.length - 1) {
      setCardVisible(false);
      setTimeout(() => { setPersonalIndex(i => i + 1); setCardVisible(true); }, 300);
    } else {
      goToAwards();
    }
  };

  const handlePrevPersonal = () => {
    if (personalIndex > 0) {
      setCardVisible(false);
      setTimeout(() => { setPersonalIndex(i => i - 1); setCardVisible(true); }, 300);
    }
  };

  const handleNextAward = () => {
    if (awardIndex < AWARD_CONFIGS.length - 1) {
      setAwardVisible(false);
      setTimeout(() => setAwardIndex(i => i + 1), 300);
    } else {
      onClose();
    }
  };

  const handlePrevAward = () => {
    if (awardIndex > 0) {
      setAwardVisible(false);
      setTimeout(() => setAwardIndex(i => i - 1), 300);
    }
  };

  const progressPct = phase === 'personal'
    ? personalCards.length > 0 ? ((personalIndex + 1) / personalCards.length) * 50 : 50
    : 50 + ((awardIndex + 1) / AWARD_CONFIGS.length) * 50;

  if (loading) {
    return (
      <div style={overlayStyle}>
        <div style={{ color: '#9ca3af', fontSize: '16px', fontFamily: 'Inter, sans-serif' }}>Loading your season recap...</div>
      </div>
    );
  }

  if (!recapData) {
    return (
      <div style={overlayStyle}>
        <div style={{ textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
          <div style={{ color: '#9ca3af', marginBottom: '24px' }}>Recap not found.</div>
          <button onClick={onClose} style={navBtnStyle}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle}>
      {showConfetti && <Confetti />}

      {/* Gold progress bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', backgroundColor: '#111' }}>
        <div style={{
          height: '100%',
          width: `${progressPct}%`,
          backgroundColor: '#d4af37',
          transition: 'width 0.6s ease',
          boxShadow: '0 0 10px rgba(212,175,55,0.5)',
        }} />
      </div>

      {/* Close */}
      <button onClick={onClose} style={{
        position: 'absolute', top: '20px', right: '20px',
        background: 'transparent', border: 'none',
        color: '#6b7280', fontSize: '22px', cursor: 'pointer', zIndex: 10,
        transition: 'color 0.2s',
      }}>✕</button>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 'clamp(16px, 3vh, 36px)', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>
          {phase === 'personal' ? '⭐ Your Season' : '🏆 Club Awards'}
        </div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#d4af37', letterSpacing: '-0.02em' }}>
          {recapData.seasonName ?? 'Season Recap'}
        </div>
      </div>

      {/* PERSONAL PHASE */}
      {phase === 'personal' && (
        <div style={{ width: '100%', maxWidth: '420px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
          {personalCards.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏌️</div>
              <div style={{ marginBottom: '24px' }}>You didn't play any rounds this season — but the club still had a blast!</div>
              <button onClick={goToAwards} style={goldBtnStyle}>See Club Awards →</button>
            </div>
          ) : (
            <>
              <PersonalCard card={personalCards[personalIndex]} visible={cardVisible} />
              <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: 'clamp(16px, 3vh, 32px)', alignItems: 'center' }}>
                <button
                  onClick={handlePrevPersonal}
                  disabled={personalIndex === 0}
                  style={{ ...navBtnStyle, opacity: personalIndex === 0 ? 0.3 : 1 }}
                >←</button>
                <span style={{ fontSize: '12px', color: '#6b7280', minWidth: '48px', textAlign: 'center' }}>
                  {personalIndex + 1} / {personalCards.length}
                </span>
                <button onClick={handleNextPersonal} style={navBtnStyle}>
                  {personalIndex < personalCards.length - 1 ? '→' : 'Awards →'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* AWARDS PHASE */}
      {phase === 'awards' && (
        <div style={{ width: '100%', maxWidth: '520px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
          <AwardCard
            config={AWARD_CONFIGS[awardIndex]}
            award={recapData.awards?.[AWARD_CONFIGS[awardIndex].key]}
            visible={awardVisible}
            index={awardIndex}
            total={AWARD_CONFIGS.length}
          />

          {/* Final card message */}
          {awardIndex === AWARD_CONFIGS.length - 1 && awardVisible && (
            <div style={{
              textAlign: 'center',
              marginTop: '20px',
              animation: 'srFadeUp 0.5s 0.3s ease both',
              fontFamily: 'Inter, sans-serif',
            }}>
              <div style={{ fontSize: '22px', fontWeight: '700', color: '#ffffff', marginBottom: '4px', letterSpacing: '-0.02em' }}>
                See you next season! 🏌️
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Thanks for a great year, KWAC.</div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: 'clamp(16px, 3vh, 32px)', alignItems: 'center' }}>
            <button
              onClick={handlePrevAward}
              disabled={awardIndex === 0}
              style={{ ...navBtnStyle, opacity: awardIndex === 0 ? 0.3 : 1 }}
            >←</button>
            <span style={{ fontSize: '12px', color: '#6b7280', minWidth: '48px', textAlign: 'center' }}>
              {awardIndex + 1} / {AWARD_CONFIGS.length}
            </span>
            <button onClick={handleNextAward} style={awardIndex === AWARD_CONFIGS.length - 1 ? goldBtnStyle : navBtnStyle}>
              {awardIndex < AWARD_CONFIGS.length - 1 ? '→' : '🏌️ Finish'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: '#0f0f0f',
  zIndex: 2000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'clamp(52px, 8vh, 64px) clamp(12px, 4vw, 20px) clamp(16px, 4vh, 40px)',
  overflow: 'hidden',
  boxSizing: 'border-box',
};

const navBtnStyle = {
  padding: '10px 22px',
  backgroundColor: '#1e1e1e',
  color: '#ffffff',
  border: '1px solid #2d2d2d',
  borderRadius: '8px',
  fontSize: '15px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: 'Inter, sans-serif',
};

const goldBtnStyle = {
  padding: '12px 28px',
  backgroundColor: '#d4af37',
  color: '#0f0f0f',
  border: 'none',
  borderRadius: '10px',
  fontSize: '15px',
  fontWeight: '700',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: 'Inter, sans-serif',
};
